import React, { useState, useEffect, useContext } from 'react'
import styled from 'styled-components'
import { useStaticQuery, graphql } from 'gatsby'
import { sum } from 'lodash'

import { selectProductVariant } from 'utils/selectProductVariant'
import * as spacing from 'styles/spacing'
import { Lead } from 'styles/typography'
import { borderColor } from 'styles/colors'
import { mediaDown } from 'styles/media'

import CheckoutContext from 'contexts/Checkout'
import Layout from 'layouts/Layout'
import Chrome from 'components/buy/BuyChrome'
import MultipackSummary from 'components/buy/MultipackSummary'
import BuyOverview from 'components/buy/BuyOverview'
import BuySection from 'components/buy/BuySection'
import BuyCartridges from 'components/buy/BuyCartridges'

const Spacer = styled.div`
  height: 176px;
`

const StyledBuySection = styled(BuySection)`
  ${mediaDown.medium`
    margin-top: ${spacing.medium};
    padding-top: ${spacing.medium};
    border-top: 1px solid ${borderColor};
    margin-bottom: ${spacing.large};
  `};
`

export default () => {
  const { addLineItemsToCart, setTinyCartVisible } = useContext(CheckoutContext)

  const { shopifyCollection } = useStaticQuery(graphql`
    query Multipack3 {
      shopifyCollection(handle: { eq: "epods" }) {
        title
        descriptionHtml
        description
        products {
          id
          title
          handle
          description
          tags
          productType
          priceRange {
            maxVariantPrice {
              amount
              currencyCode
            }
            minVariantPrice {
              amount
              currencyCode
            }
          }
          images {
            localFile {
              absolutePath
            }
            originalSrc
          }
          variants {
            id
            title
            price
            sku
            image {
              originalSrc
              localFile {
                absolutePath
              }
            }
            availableForSale
            selectedOptions {
              name
            }
          }
        }
      }
    }
  `)

  let fetchedCartridges = shopifyCollection.products.filter(p => p.productType === 'epod pods')
  fetchedCartridges = selectProductVariant({ products: fetchedCartridges, noneSelected: true })

  const [cartridges, setCartridges] = useState(fetchedCartridges)
  const [flavours, setFlavours] = useState([])

  useEffect(() => {
    const selectedFlavours = cartridges
      .map(cartridge => cartridge.variants)
      .flat()
      .filter(variant => variant.quantity > 0)

    const upToDateFlavour = []
    selectedFlavours.forEach(flavour => {
      ;[...Array(flavour.quantity).keys()].forEach(() => {
        upToDateFlavour.push({
          name: cartridges.find(cartridge => cartridge.variants.find(variant => variant.id === flavour.id)).title,
          figure: cartridges.find(cartridge => cartridge.variants.find(variant => variant.id === flavour.id)).images[0]
            .originalSrc
        })
      })
    })

    setFlavours(upToDateFlavour)
  }, [cartridges])

  const handleAddCartridge = (product, variants) => {
    const upToDateCartridges = []

    cartridges.forEach(cartridge => {
      const upToDateVariants = cartridge.id === product.id ? variants : cartridge.variants
      const upToDateCartridge = {
        ...cartridge,
        variants: upToDateVariants
      }

      upToDateCartridges.push(upToDateCartridge)
    })

    setCartridges(upToDateCartridges)
  }

  const handleCartridgeRemove = cartridgeId => {
    const upToDateCartridges = [...cartridges]
    const cartridgeIndex = upToDateCartridges.findIndex(c => c.id === cartridgeId)
    upToDateCartridges[cartridgeIndex].variants.forEach(v => {
      v.quantity = 0
    })
    setCartridges(upToDateCartridges)
  }

  const onAdd = async () => {
    const selectedFlavours = cartridges
      .map(cartridge => cartridge.variants)
      .flat()
      .filter(variant => variant.quantity > 0)

    const lineItems = selectedFlavours.map(flavour => {
      return {
        variantId: flavour.id,
        quantity: flavour.quantity,
        customAttributes: [
          {
            key: 'discount',
            value: `multipack-3`
          }
        ]
      }
    })

    await addLineItemsToCart(lineItems)
    setCartridges(fetchedCartridges)
    setFlavours([])

    setTinyCartVisible(true)
    setTimeout(() => {
      setTinyCartVisible(false)
    }, 5000)
  }

  const currentCartridgeQuantity = () => {
    return sum(cartridges.map(cartridge => cartridge.variants.map(variant => variant.quantity)).flat())
  }

  return (
    <Layout fullHeight lightFooter hideHeader>
      <Chrome
        photos={[{ originalSrc: `${process.env.BASE_PATH}/images/misc/spacer.png` }]}
        backgroundSrc={`${process.env.BASE_PATH}/images/products/epod/multipack/3-pack-large.jpg`}
        summary={<MultipackSummary flavours={flavours} saving="17%" price="33.00" limit={3} onAdd={onAdd} />}
        relatedProducts={<Spacer className="u-hide-medium-down" />}
      >
        <BuyOverview
          title="Mix & Match: 3 Pack Bundle"
          description="Select 3 packs of your choice and <strong>save 15%</strong></br><small>*Cannot be combined with other promotions or the 5 pack bundle.</small>"
          isMultipack
        />

        <StyledBuySection>
          <p>
            <strong>Pack (2 pods)</strong>
          </p>
          <Lead>Choose 3 packs to add to your bundle</Lead>
        </StyledBuySection>

        <BuyCartridges
          cartridges={cartridges}
          handleAddCartridge={handleAddCartridge}
          handleCartridgeRemove={handleCartridgeRemove}
          currentCartridgeQuantity={currentCartridgeQuantity}
          multipackLimit={3}
        />
      </Chrome>
    </Layout>
  )
}
